"use client";
import { HUBSPOT_PAGES, PROZ_SOCIALS } from "@/constants/common";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React from "react";
import FooterHubSpot from "./footerHubSpot";
import Image from "next/image";
import { useLocalization } from "@/providers/LocalizationProvider";

const Footer = () => {
  const dict = useLocalization();

  const pathname = usePathname() ?? "";
  // Hide footer on all admin pages
  if (pathname.startsWith("/admin") || pathname.startsWith("/next/admin")) {
    return null;
  }
  if (pathname.includes("/session/")) {
    return null;
  }
  if (HUBSPOT_PAGES.includes(pathname)) {
    return <FooterHubSpot />;
  }
  const year = new Date().getFullYear();
  return (
    <footer className="bg-primary dark:bg-dark-footer overflow-hidden">
      <div className="max-w-7xl mx-auto gap-2 px-4 py-8">
        {/*Menu items*/}
        <div className="grid grid-cols-1 md:grid-cols-3 sm:gap-1 pb-3">
          <div className="md:col-span-1 mb-3">
            <h3 className="w-full text-base font-semibold text-accent-light mb-3">
              {dict?.footer?.footer_find_freelance_linguists ?? "Find freelance linguists"}
            </h3>
            <ul className="mb-3">
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/providers/translators">
                  {dict?.footer?.footer_certified_translators ?? "Certified translators"}
                </Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/providers/interpreters">
                  {dict?.footer?.footer_interpreters ?? "Interpreters"}
                </Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/providers/subtitlers">
                  {dict?.footer?.footer_subtitlers ?? "Subtitlers"}
                </Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/providers">{dict?.footer?.footer_more ?? "More"}</Link>
              </li>
            </ul>
            <h3 className="w-full text-base font-semibold text-accent-light mb-3">
              {dict?.footer?.footer_find_language_companies ?? "Find language companies"}
            </h3>
            <ul className="mb-3">
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/providers/lscs">
                  {dict?.footer?.footer_business_directory ?? "Business directory"}
                </Link>
              </li>
            </ul>
          </div>
          <div className="md:col-span-1 mb-3">
            <h3 className="w-full text-base font-semibold text-accent-light mb-3">
              {dict?.footer?.footer_for_freelance_professionals ?? "For freelance professionals"}
            </h3>
            <ul className="">
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/opportunities">
                  {dict?.footer?.footer_opportunities_marketplace ?? "Opportunities marketplace"}
                </Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/learn/courses">
                  {dict?.footer?.footer_training_certification ?? "Training & certification"}
                </Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="https://www.proz.com/invoice">
                  {dict?.footer?.footer_invoicing ?? "Invoicing"}
                </Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/prozpay/about">{dict?.footer?.footer_payments ?? "Payments"}</Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="https://www.proz.com/search">
                  {dict?.footer?.footer_terminology ?? "Terminology"}
                </Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="https://www.proz.com/events_calendar">
                  {dict?.footer?.footer_events ?? "Events"}
                </Link>
              </li>
            </ul>
          </div>
          <div className="md:col-span-1 mb-3">
            <h3 className="w-full text-base font-semibold text-accent-light mb-3">
              {dict?.footer?.footer_about_proz ?? "About ProZ"}
            </h3>
            <ul className="">
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/about/overhaul">
                  {dict?.footer?.footer_about_this_new_site ?? "About this new site"}
                </Link>
              </li>
              <li className="text-white ms-0 mb-1 fw-light text-sm">
                <Link href="/about">{dict?.footer?.footer_about_proz ?? "About ProZ"}</Link>
              </li>
              <li className="text-white ms-0</Link> mb-1 fw-light text-sm">
                <Link href="http://proz.com/">
                  {dict?.footer?.footer_back_to_classic_site ?? "Back to classic site"}
                </Link>
              </li>

              {/* Get help and Accessibility buttons */}
              <li className="flex fw-light gap-4 items-center mb-1 ms-0 mt-10 text-sm text-white">
                <Link
                  className="text-primary text-md bg-white px-4 py-2 rounded-xl whitespace-nowrap"
                  href="/support-center"
                >
                  {dict?.footer?.footer_get_help_button ?? "Get help"}
                </Link>
                <Link
                  className="text-primary text-md bg-white px-4 py-2 rounded-xl whitespace-nowrap"
                  href="https://www.proz.com/accessibility-settings"
                >
                  {dict?.footer?.footer_accessibility_button ?? "Accessibility"}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/*Copyright*/}
        <hr className="border-1 mb-2" />
        <div className="flex flex-col lg:flex-row lg:justify-between justify-center items-center mt-3 gap-3 ">
          <div className="">
            <Link href="#" className="logo-footer">
              <Image
                src="/next/next_assets/images/logo.svg"
                className="h-8 w-auto"
                alt=""
                width={10}
                height={10}
              />
            </Link>
          </div>

          <div className="">
            <p className="text-white text-center dark:text-gray-400">
              Copyright © 1999 - {year} ProZ -{" "}
              {dict?.footer?.footer_copyright ?? "All rights reserved"}.
            </p>
          </div>

          <div className="">
            <ul className="flex flex-row list-none foot-icon ltr:md:text-right  gap-4">
              <li className="inline">
                <Link href={PROZ_SOCIALS.facebook} target="_blank">
                  <Image
                    src="/next/next_assets/images/svg/facebook-transparent.svg"
                    alt="social-logo"
                    height={25}
                    width={25}
                  />
                </Link>
              </li>
              <li className="inline">
                <Link href={PROZ_SOCIALS.linkedin} target="_blank">
                  <Image
                    src="/next/next_assets/images/svg/linkedin-transparent.svg"
                    alt="social-logo"
                    height={25}
                    width={25}
                  />
                </Link>
              </li>
              <li className="inline">
                <Link href={PROZ_SOCIALS.twitter} target="_blank">
                  <Image
                    src="/next/next_assets/images/svg/twitter-transparent.svg"
                    alt="social-logo"
                    height={25}
                    width={25}
                  />
                </Link>
              </li>
              <li className="inline">
                <Link href={PROZ_SOCIALS.instagram} target="_blank">
                  <svg
                    fill="#fff"
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 169.063 169.063"
                    xmlSpace="preserve"
                  >
                    <g strokeWidth="0" />
                    <g strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M122.406 0H46.654C20.929 0 0 20.93 0 46.655v75.752c0 25.726 20.929 46.655 46.654 46.655h75.752c25.727 0 46.656-20.93 46.656-46.655V46.655C169.063 20.93 148.133 0 122.406 0m31.657 122.407c0 17.455-14.201 31.655-31.656 31.655H46.654C29.2 154.063 15 139.862 15 122.407V46.655C15 29.201 29.2 15 46.654 15h75.752c17.455 0 31.656 14.201 31.656 31.655z" />
                    <path d="M84.531 40.97c-24.021 0-43.563 19.542-43.563 43.563 0 24.02 19.542 43.561 43.563 43.561s43.563-19.541 43.563-43.561c0-24.021-19.542-43.563-43.563-43.563m0 72.123c-15.749 0-28.563-12.812-28.563-28.561s12.813-28.563 28.563-28.563 28.563 12.813 28.563 28.563-12.814 28.561-28.563 28.561m45.39-84.842c-2.89 0-5.729 1.17-7.77 3.22a11.05 11.05 0 0 0-3.23 7.78c0 2.891 1.18 5.73 3.23 7.78 2.04 2.04 4.88 3.22 7.77 3.22 2.9 0 5.73-1.18 7.78-3.22 2.05-2.05 3.22-4.89 3.22-7.78 0-2.9-1.17-5.74-3.22-7.78-2.04-2.05-4.88-3.22-7.78-3.22" />
                  </svg>
                </Link>
              </li>
              <li className="inline">
                <Link href={PROZ_SOCIALS.youtube} target="_blank">
                  <svg
                    fill="#fff"
                    width="25"
                    height="25"
                    viewBox="0 0 1024 1024"
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon"
                    stroke="#fff"
                    strokeWidth=".01"
                  >
                    <g strokeWidth="0" />
                    <g
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      stroke="#CCC"
                      strokeWidth="2.048"
                    />
                    <path d="M941.3 296.1a112.3 112.3 0 0 0-79.2-79.3C792.2 198 512 198 512 198s-280.2 0-350.1 18.7A112.12 112.12 0 0 0 82.7 296C64 366 64 512 64 512s0 146 18.7 215.9c10.3 38.6 40.7 69 79.2 79.3C231.8 826 512 826 512 826s280.2 0 350.1-18.8c38.6-10.3 68.9-40.7 79.2-79.3C960 658 960 512 960 512s0-146-18.7-215.9zM423 646V378l232 133z" />
                  </svg>
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
